import React, { useState, useEffect } from 'react';
import axios from 'axios';
import uniqBy from 'lodash/uniqBy';

import { ICareersBlockFields } from '../../../types/contentful';

import { DepartmentBlock } from './DepartmentBlock';
import { Dropdown } from './Dropdown';
import classNames from 'classnames';

export interface Department {
  title: string;
  postings: AvailableJob[];
  key: string;
}

interface AvailableJob {
  hostedUrl: string;
  text: string;
  key: string;
}

interface Filters {
  departmentFilters: string[] | null;
  locationFilters: string[] | null;
}

export function CareersBlock({
  supportingCopy,
  headline
}: ICareersBlockFields) {
  const [allOpenings, setAllOpenings] = useState<Department[]>([]);
  const [openings, setOpenings] = useState<Department[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    departmentFilters: null,
    locationFilters: null
  });

  // Get the list of jobs from Lever.co API
  useEffect(() => {
    const LEVER_API_URL =
      'https://api.lever.co/v0/postings/greenlight?&group=team&mode=json';

    const fetchData = async () => {
      const result = await axios.get(LEVER_API_URL);

      let allPostings: any = [];
      const openDepartments: Department[] = [];

      result.data.forEach((returnedDepartment: Department) => {
        allPostings = [...allPostings, ...returnedDepartment.postings];
        openDepartments.push(returnedDepartment);
      });

      setAllOpenings(openDepartments);
      setOpenings(openDepartments);
      setDepartments(openDepartments.map(({ title }: Department) => title));
      setLocations(
        uniqBy(allPostings, 'categories.location').map(
          ({ categories }: any) => categories.location
        )
      );
    };

    fetchData();
  }, []);

  useEffect(() => {
    const { departmentFilters, locationFilters } = filters;

    let filteredJobs: Department[] = JSON.parse(JSON.stringify(allOpenings));

    if (departmentFilters && departmentFilters.length) {
      filteredJobs = filteredJobs.filter(({ title }: Department) =>
        departmentFilters.includes(title)
      );
    }

    if (locationFilters && locationFilters.length) {
      filteredJobs = filteredJobs.reduce(
        (prev: Department[], curr: Department) =>
          prev.concat({
            ...curr,
            postings: curr.postings.filter(({ categories }: any) =>
              locationFilters.includes(categories.location)
            )
          }),
        []
      );
    }

    setOpenings(
      filteredJobs.filter(({ postings }: Department) => postings.length)
    );
  }, [filters.departmentFilters, filters.locationFilters, allOpenings]);

  type FilterType = keyof Filters; // 'departmentFilters' | 'locationFilters'

  const handleFilterSelection = (
    selection: string | null,
    filter: 'department' | 'location'
  ) => {
    setFilters((state) => {
      const filterKey: FilterType =
        filter === 'department' ? 'departmentFilters' : 'locationFilters';
      const currentSelections = state[filterKey];
      let newSelections: string[] | null;

      if (selection === null) {
        // "Select all" was clicked - toggle between "select all" and "deselect all"
        if (currentSelections === null) {
          // Currently in "select all" state, toggle to "deselect all" (empty array)
          newSelections = [];
        } else {
          // Currently in some selection state, go to "select all"
          newSelections = null;
        }
      } else if (currentSelections === null) {
        // "Select all" is active, user clicked on individual item - uncheck that item
        const allOptions = filter === 'department' ? departments : locations;
        newSelections = allOptions.filter((item) => item !== selection);
      } else if (currentSelections.includes(selection)) {
        // Individual item was deselected
        newSelections = currentSelections.filter(
          (item: string) => item !== selection
        );
      } else {
        // Individual item was selected
        newSelections = [...currentSelections, selection];
      }

      return { ...state, [filterKey]: newSelections };
    });
  };

  return (
    <div id="careersDropdown">
      <div className="layout-padding flex w-full flex-col items-center gap-6 rounded-lg bg-white pb-10 pt-16 tablet:pb-16">
        {/** Headline */}
        <div className="flex flex-col items-center gap-3 self-stretch tablet:gap-4 tablet:self-auto tablet:px-4">
          <h1 className="typography-display-2 text-center">{headline}</h1>
          <p className="typography-display-5 text-center">{supportingCopy}</p>
        </div>
        {/** End Headline */}
        {/** Dropdown Filter */}
        <div className="flex flex-col items-center justify-center gap-4 self-stretch tablet:w-full tablet:flex-row tablet:gap-lg tablet:self-auto">
          <Dropdown
            defaultSelection="Select all"
            filter="department"
            currentSelections={filters.departmentFilters}
            dropdownText={
              filters.departmentFilters === null
                ? 'Departments'
                : filters.departmentFilters.length === 0
                ? 'Departments (0)'
                : `Departments (${filters.departmentFilters.length})`
            }
            dropdownOptions={departments}
            handleSelection={handleFilterSelection}
            data-testid="department-dropdown"
          />
          <Dropdown
            defaultSelection="Select all"
            filter="location"
            currentSelections={filters.locationFilters}
            dropdownText={
              filters.locationFilters === null
                ? 'Locations'
                : filters.locationFilters.length === 0
                ? 'Locations (0)'
                : `Locations (${filters.locationFilters.length})`
            }
            dropdownOptions={locations}
            handleSelection={handleFilterSelection}
            data-testid="location-dropdown"
          />
        </div>
        {/** End Dropdown Filter */}
        {/** Job Listings */}
        <div
          className={classNames(
            'flex flex-col items-center gap-6 self-stretch tablet:block tablet:w-full tablet:max-w-[1152px] tablet:items-start tablet:space-y-6 tablet:self-auto',
            {
              'tablet:columns-w-1/2 tablet:columns-2': openings.length
            }
          )}
        >
          {openings.length ? (
            <>
              {openings.map((department: Department) => (
                <DepartmentBlock
                  key={department.title}
                  title={department.title}
                  availableJobs={department.postings}
                />
              ))}
            </>
          ) : (
            <p
              className="typography-display-4 mx-auto mt-6 w-full p-6 text-center tablet:max-w-[888px]"
              data-testid="no-job-postings"
            >
              {
                'Your search turned 0 results — try adjusting your filters, or come back soon!'
              }
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
